import { gql } from '@apollo/client';
import { Checkbox } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import EmailLink from '../../../components/EmailLink';
import PersonTagIcon from '../../../components/PersonTagIcon';
import TableCell from '../../../components/TableCell';
import Text from '../../../components/Text';
import TooltipIcon from '../../../components/TooltipIcon';
import { SoulListItemFragment } from '../../../generated/graphql';
import { noop } from '../../../helpers/utils';
import { useNavigateSafely } from '../../../hooks/useNavigateSafely';
import { ListItemComponent } from '../../../types';

export type SoulListProps = {
  item: SoulListItemFragment;
  skeleton?: boolean;
} & ListItemComponent;

const SoulListItem = ({ item, skeleton, selected, onToggle }: SoulListProps) => {
  const buildNavigateSafely = useNavigateSafely();
  return (
    <tr onClick={onToggle || skeleton ? noop : buildNavigateSafely(`/souls/${item!.id}`)}>
      {onToggle && (
        <TableCell>
          <Checkbox inline disabled={skeleton} checked={selected} onChange={() => onToggle(item.id)} className="m-0" />
        </TableCell>
      )}
      <TableCell>
        <Text skeleton={skeleton}>{item.display_name}</Text>
        <br />
        <Text skeleton={skeleton} small muted>
          {item.organisation && item.organisation.name}
        </Text>
      </TableCell>
      <TableCell>
        {item.meta.buys_ticket && (
          <TooltipIcon label="Selbstzahler" icon={IconNames.EURO} skeleton={skeleton} marginRight />
        )}
        {item.meta.needs_wheelchair && (
          <TooltipIcon label="Rollstuhl" icon={IconNames.SHOPPING_CART} skeleton={skeleton} marginRight />
        )}
        {item.meta.needs_wheeled_walker && (
          <TooltipIcon label="Rollator" icon={IconNames.WALK} skeleton={skeleton} marginRight />
        )}
        {item.meta.needs_blind && (
          <TooltipIcon label="Blind" icon={IconNames.EYE_OFF} skeleton={skeleton} marginRight />
        )}
        {item.meta.needs_drive && (
          <TooltipIcon
            label="Muss von Zuhause abgeholt werden"
            icon={IconNames.DRIVE_TIME}
            skeleton={skeleton}
            marginRight
          />
        )}
        {item.meta.needs_trainstation_fetch && (
          <TooltipIcon
            label="Muss vom Bahnhof abgeholt werden"
            icon={IconNames.TRAIN}
            skeleton={skeleton}
            marginRight
          />
        )}
        {item.tags.map((tag) => (
          <PersonTagIcon key={tag.id} tag={tag} skeleton={skeleton} />
        ))}
      </TableCell>
      <TableCell>
        <Text skeleton={skeleton} muted>
          {item.phone || item.mobile || '—'}
        </Text>
      </TableCell>
      <TableCell>
        <EmailLink email={item.email} skeleton={skeleton} />
      </TableCell>
    </tr>
  );
};

SoulListItem.fragments = {
  soul: gql`
    fragment SoulListItem on Soul {
      id
      status
      display_name
      email
      phone
      mobile
      meta {
        buys_ticket
        needs_wheelchair
        needs_wheeled_walker
        needs_drive
        needs_trainstation_fetch
        needs_blind
      }
      organisation {
        id
        name
      }
      tags {
        ...PersonTagIcon
      }
    }
    ${PersonTagIcon.fragments.tag}
  `,
};

export default SoulListItem;
